import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    handleEvent,
    JazzRoomEventAddTrack,
    JazzRoomEventDominantSpeakerChanged, JazzRoomEventRemoveTrack,
    JazzRoomEventTrackMuteChanged
} from '@salutejs/jazz-sdk-web';
import {
    getInitialWeightMap,
    removeParticipantWeight,
    sum,
    updateDominantWeight,
    updateParticipantWeight,
    WeightMapType
} from '../participantWeights';

export const useSortedParticipants = ({ room, participants }) => {
    const [weightMap, setWeightMap] = useState<WeightMapType>(() => new Map());

    const sortedParticipants = useMemo(() => {
        return participants.sort((a, b) => {
            const aWeight = sum(weightMap.get(a.id));
            const bWeight = sum(weightMap.get(b.id));
            return bWeight - aWeight;
        });
    }, [participants, weightMap]);

    const onDominantSpeakerChanged = useCallback(({ payload }: JazzRoomEventDominantSpeakerChanged) => {
        const { id: participantId, prevId: prevParticipantId } = payload;
        setWeightMap(updateDominantWeight({ participantId, prevParticipantId }));
    }, []);
    const onParticipantAddOrMuteTrack = useCallback(({ payload }: JazzRoomEventAddTrack | JazzRoomEventTrackMuteChanged) => {
        const { mediaType, participantId, isMuted } = payload;
        setWeightMap(updateParticipantWeight({ mediaType, participantId, isMuted }));
    }, []);
    const onParticipantRemoveTrack = useCallback(({ payload }: JazzRoomEventRemoveTrack) => {
        const { participantId } = payload
        setWeightMap(removeParticipantWeight({ participantId }));
    }, []);

    useEffect(() => {
        if (!room || participants.length === 0) return;
        setWeightMap(getInitialWeightMap({ room, participants }));

        const unsubscribeDominantParticipantId = handleEvent(room.event$, 'dominantSpeakerChanged', onDominantSpeakerChanged);
        const unsubscribeParticipantAddTrack = handleEvent(room.event$, 'addTrack', onParticipantAddOrMuteTrack);
        const unsubscribeParticipantMuteTrack = handleEvent(room.event$, 'trackMuteChanged', onParticipantAddOrMuteTrack);
        const unsubscribeParticipantRemoveTrack = handleEvent(room.event$, 'removeTrack', onParticipantRemoveTrack);

        return () => {
            unsubscribeDominantParticipantId();
            unsubscribeParticipantAddTrack();
            unsubscribeParticipantMuteTrack();
            unsubscribeParticipantRemoveTrack();
        }
    }, [room, participants]);

    return { sortedParticipants };
}
